// Copyright (c) 2025 Signal from Noise, Inc. All rights reserved.
//
// This software is the confidential and proprietary information of
// Signal from Noise, Inc. You shall not disclose such Confidential Information
// and shall use it only in accordance with the terms of a separate license
// agreement entered into with Signal from Noise, Inc.
//
// Authors: /AUTHORS

import { fileExtension, libraryContentType } from "@repo/mime";
import { stAnalytics } from "@storytell/analytics";
import {
  CreateAssetHttpRequestConfig,
  type CreateAssetHttpResponse,
  UploadCompleteRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import type { Asset, AssetId } from "@storytell/types";
import { createEffect, createSignal, onCleanup } from "solid-js";
import { isServer } from "solid-js/web";
import SparkMD5 from "spark-md5";
import { lifecycleStateGrouped } from "~/components/utils/lifecycleStateMappers";
import { gateCreditsDescriptions } from "~/hooks/useGateActionByCredits";
import { useProjectBillingContext } from "~/hooks/useProjectBillingContext";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";

export interface FileUploadState {
  id: string;
  file: File;
  projectId: string;
  status: "pending" | "uploading" | "processing" | "completed" | "error";
  error?: string;
  assetId?: string;
  /** Signed URL for large file uploads */
  signedUrl?: string;
  /** Upload headers for signed URL uploads */
  uploadHeaders?: Record<string, string>;
  /** MD5 hash of the file for duplicate detection */
  md5Hash?: string;
  /** The content type the library takes the file as, resolved when it was accepted */
  mimeType: string;
}

interface FileUploadConfig {
  /** Maximum file size in bytes (default: 100MB) */
  maxFileSize?: number;
  maxConcurrent?: number;
  getAuthToken?: () => string | undefined;
  onAssetCreated?: (assetId: string, asset: Asset) => void;
  onAssetStatusChanged?: (
    assetId: string,
    status: "uploading" | "processing" | "completed" | "error",
    asset: Asset | null
  ) => void;
  /** Callback called when duplicate files, in-queue files, or validation errors are detected */
  onError?: (error: {
    type: "duplicate" | "in-queue" | "validation";
    message: string;
    filenames: string[];
    /** Asset IDs that the caller may inject into chat scope. Populated for "duplicate" (library matches) and "in-queue" (in-flight uploads with assigned asset IDs). Not parallel to filenames — only contains resolved IDs. */
    assetIds?: string[];
  }) => void;
}

export interface UseFileUploadReturn {
  uploads: () => FileUploadState[];
  uploadFiles: (files: File[], projectId: string) => Promise<void>;
  /**
   * Open the insufficient-credits modal and return true if the user's balance
   * is at or below zero. Returns false otherwise. Call this before any UI
   * side effects (e.g. creating temp assets, opening progress modal) so the
   * gate blocks the flow before it visibly begins. uploadFiles also calls
   * this internally as a safety net for callers that bypass it.
   */
  tryGateUploadByCredits: () => boolean;
  /** Remove upload(s) that correspond to a deleted asset (keeps upload modal in sync with assets page) */
  removeUploadByAssetId: (assetId: string) => void;
}

/**
 * Get default configuration with lazy MIME type resolution
 */
function getDefaultConfig() {
  return {
    maxFileSize: 100 * 1024 * 1024,
    maxConcurrent: 3,
    getAuthToken: () => undefined,
  } satisfies FileUploadConfig;
}

function generateUploadId(): string {
  return `upload_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Calculate MD5 checksum for file integrity checking with GCP Cloud Storage
 */
const computeMD5Checksum = async (file: File): Promise<string> => {
  if (isServer) {
    throw new Error("MD5 checksum calculation is not available on the server");
  }
  // Hashed in chunks so only one chunk of a large file sits in memory, and the main thread gets a
  // turn between chunks.
  const chunkBytes = 4 * 1024 * 1024;
  const spark = new SparkMD5.ArrayBuffer();
  for (let offset = 0; offset < file.size; offset += chunkBytes) {
    spark.append(await file.slice(offset, offset + chunkBytes).arrayBuffer());
  }
  return hexToBase64(spark.end());
};

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = Number.parseInt(hex.substring(i, i + 2), 16);
  }
  return bytes;
}

function binaryToBase64(binary: string): string {
  return btoa(binary);
}

function hexToBase64(hexString: string): string {
  const bytes = hexToBytes(hexString);
  const binaryString = Array.from(bytes)
    .map((byte) => String.fromCharCode(byte))
    .join("");
  return binaryToBase64(binaryString);
}

/**
 * Normalize an MD5 hash to base64 so server (hex) and client (base64) values
 * can be compared with ===.  Returns the input unchanged if it already looks
 * like base64 (contains non-hex chars or ends with '=').
 */
function normalizeMD5ToBase64(md5: string): string {
  // Already base64 (contains +, /, = or uppercase letters beyond A-F)
  if (/[+/=G-Zg-z]/.test(md5)) return md5;
  // Looks like hex (32 hex chars for MD5)
  if (/^[0-9a-fA-F]{32}$/.test(md5)) return hexToBase64(md5);
  return md5;
}

/** The content type to upload the file as, or why it can't be uploaded. */
function validateFile(file: File, config: FileUploadConfig): { mimeType: string } | { error: string } {
  const defaultConfig = getDefaultConfig();

  if (file.size > (config.maxFileSize || defaultConfig.maxFileSize)) {
    return {
      error: `File size exceeds maximum allowed size of ${(config.maxFileSize || defaultConfig.maxFileSize) / (1024 * 1024)}MB`,
    };
  }

  const mimeType = libraryContentType(file);
  if (!mimeType) {
    return { error: `File type '${file.type || fileExtension(file.name) || "unknown"}' is not supported` };
  }

  return { mimeType };
}

/**
 * Hook for handling file uploads
 */
export function useFileUpload(userConfig: FileUploadConfig = {}): UseFileUploadReturn {
  const wire = useWire();
  const config = { ...getDefaultConfig(), ...userConfig };
  const [uploads, setUploads] = createSignal<FileUploadState[]>([]);

  if (isServer) {
    return {
      uploads,
      uploadFiles: async () => {
        /* no-op: SSR stub */
      },
      tryGateUploadByCredits: () => false,
      removeUploadByAssetId: () => {
        /* no-op: SSR stub */
      },
    };
  }

  // Project billing context drives the credit gate. In a project the paying
  // org may differ from the caller's home org (billing_mode=explicit), so we
  // gate on the project's balance rather than wire.billing.credits().
  const { data: projectBilling } = useProjectBillingContext(() => wire.currentProjectId());

  const tryGateUploadByCredits = (): boolean => {
    const projectCtx = projectBilling();
    const inProject = wire.currentProjectId() !== undefined;

    let shouldGate = false;
    if (inProject) {
      shouldGate = projectCtx !== undefined && projectCtx.balance <= 0;
    } else if (wire.billing.show() && !wire.billing.loading()) {
      shouldGate = wire.billing.credits() <= 0;
    }

    if (shouldGate) {
      wire.ui.creditsGate.openModal(gateCreditsDescriptions.fileUpload);
      return true;
    }
    return false;
  };

  const activeRequests = new Map<string, AbortController>();

  // Synchronous tracking of filenames currently inside uploadFiles (covers the
  // gap between entering uploadFiles and the async setUploads call, which is
  // where the race condition lived — two concurrent calls both saw empty uploads()).
  const filesInPipeline = new Set<string>();

  /**
   * When an upload is in "processing" and the asset in the store becomes ready, mark the upload as "completed"
   * so the modal and UI show the correct status (assets store is updated on refetch after backend finishes).
   */
  createEffect(() => {
    const list = uploads();
    const assets = wire.assets;
    const idsToComplete = new Set<string>();
    for (const upload of list) {
      if (upload.status !== "processing" || !upload.assetId) continue;
      const asset = assets.get(upload.assetId);
      const state = asset?.status?.state ?? "UNSPECIFIED";
      if (lifecycleStateGrouped(state as Parameters<typeof lifecycleStateGrouped>[0]) === "ready") {
        idsToComplete.add(upload.id);
      }
    }
    if (idsToComplete.size > 0) {
      setUploads((prev) => prev.map((u) => (idsToComplete.has(u.id) ? { ...u, status: "completed" as const } : u)));
    }
  });

  async function createAsset(
    file: File,
    mimeType: string,
    md5Hash: string,
    projectId: string
  ): Promise<{
    assetId: string;
    uploadUrl?: string;
    uploadHeaders?: Record<string, string>;
    asset: CreateAssetHttpResponse;
  }> {
    const httpClient = useStorytellClient();
    const authToken = config.getAuthToken?.();

    const requestBuilder = CreateAssetHttpRequestConfig.withBearerToken(authToken);

    const requestBody = {
      name: file.name,
      filename: file.name,
      fileSizeInBytes: file.size,
      mimeType,
      md5: md5Hash,
      projectId: projectId,
      description: "",
    };

    const result = await httpClient.fetch(requestBuilder, undefined, requestBody);

    if (result.isErr()) {
      console.error("Failed to create asset:", result.error);
      throw new Error(`Failed to create asset: ${String(result.error)}`);
    }

    const response = result.value;

    return {
      assetId: response.id,
      uploadUrl: response.signedUrl,
      uploadHeaders: undefined, // HTTP API doesn't return custom headers
      asset: response,
    };
  }

  async function uploadFileSignedUrl(uploadState: FileUploadState): Promise<void> {
    if (!uploadState.signedUrl) {
      throw new Error("Signed URL not available for large file upload");
    }

    const abortController = new AbortController();
    activeRequests.set(uploadState.id, abortController);

    try {
      const xhr = new XMLHttpRequest();

      abortController.signal.addEventListener("abort", () => {
        xhr.abort();
      });

      xhr.open("PUT", uploadState.signedUrl);

      if (uploadState.uploadHeaders) {
        Object.entries(uploadState.uploadHeaders).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      }

      const completion = new Promise<void>((resolve, reject) => {
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        });
        xhr.addEventListener("error", () => {
          reject(new Error("Upload failed due to network error"));
        });
        xhr.addEventListener("abort", () => {
          reject(new Error("Upload aborted"));
        });
      });

      xhr.send(uploadState.file);

      await completion;
    } finally {
      activeRequests.delete(uploadState.id);
    }
  }

  async function notifyUploadComplete(assetId: string, _md5Hash: string, _fileSize: number): Promise<void> {
    const httpClient = useStorytellClient();
    const authToken = config.getAuthToken?.();

    const requestBuilder = UploadCompleteRequestConfig.withBearerToken(authToken);

    const result = await httpClient.fetch(requestBuilder, { assetId: assetId as AssetId }, undefined);

    if (result.isErr()) {
      throw new Error(`Failed to notify upload completion: ${String(result.error)}`);
    }

    setUploads((prev) =>
      prev.map((upload) => (upload.assetId === assetId ? { ...upload, status: "processing" as const } : upload))
    );

    void stAnalytics.track("file_uploaded", {
      organizationId: wire.auth.realm().organizationId,
      tenantId: wire.auth.realm().tenantId,
      projectId: wire.userContext.get()?.projectId ?? "",
      threadId: wire.chat.getChatId() || null,
      threadPromptCount: 0,
      asset_id: assetId,
    });
  }

  async function processFileUpload(uploadState: FileUploadState): Promise<void> {
    try {
      setUploads((prev) =>
        prev.map((upload) => (upload.id === uploadState.id ? { ...upload, status: "uploading" as const } : upload))
      );

      let md5Hash = uploadState.md5Hash;
      if (!md5Hash) {
        md5Hash = await computeMD5Checksum(uploadState.file);
      }

      const assetData = await createAsset(uploadState.file, uploadState.mimeType, md5Hash, uploadState.projectId);

      setUploads((prev) =>
        prev.map((upload) =>
          upload.id === uploadState.id
            ? {
                ...upload,
                assetId: assetData.assetId,
                signedUrl: assetData.uploadUrl,
                uploadHeaders: assetData.uploadHeaders,
              }
            : upload
        )
      );

      if (config.onAssetCreated) {
        config.onAssetCreated(assetData.assetId, assetData.asset);
      }

      if (config.onAssetStatusChanged) {
        config.onAssetStatusChanged(assetData.assetId, "uploading", null);
      }

      const updatedUploadState = uploads().find((upload) => upload.id === uploadState.id);
      if (!updatedUploadState) {
        throw new Error("Upload state not found after asset creation");
      }
      await uploadFileSignedUrl(updatedUploadState);
      await notifyUploadComplete(assetData.assetId, md5Hash, uploadState.file.size);
    } catch (error) {
      console.error("File upload failed:", error);
      setUploads((prev) =>
        prev.map((upload) =>
          upload.id === uploadState.id
            ? {
                ...upload,
                status: "error" as const,
                error: error instanceof Error ? error.message : "Upload failed",
              }
            : upload
        )
      );
    }
  }

  const uploadFiles = async (files: File[], projectId: string): Promise<void> => {
    if (tryGateUploadByCredits()) return;
    try {
      void stAnalytics.track("file_upload_started", {
        organizationId: wire.auth.realm().organizationId,
        tenantId: wire.auth.realm().tenantId,
        projectId: wire.userContext.get()?.projectId ?? "",
        threadId: wire.chat.getChatId() || null,
        threadPromptCount: 0,
      });
      trackFeature({ feature: "assets.upload", via: "button" });
    } catch (_error) {
      /* intentionally swallowed — telemetry failure should not block upload */
    }
    const validFiles: Array<{ file: File; mimeType: string; md5Hash: string | undefined }> = [];
    const errors: string[] = [];
    const duplicates: string[] = [];
    const duplicateAssetIds: string[] = [];
    const inQueue: string[] = [];
    const inQueueAssetIds: string[] = [];

    // Check for duplicates against the server (not just the client-side LRU cache).
    // wire.assets.all() only returns ~100 cached assets, so for large libraries most
    // assets would be missed. Instead, search the server for each unique filename.
    const uniqueFilenames = [...new Set(files.map((f) => f.name))];

    // Library asset IDs keyed by filename and MD5: a dropped file is a duplicate only when both match,
    // so the same content under a different filename is allowed through.
    const libraryAssetIds = new Map<string, string>();
    const addLibraryAsset = (asset: Asset) => {
      // An AWAITING_UPLOAD row is written with the file's MD5 before any bytes go up, and nothing
      // deletes it or hides it from the listing, so a transfer that died part way would refuse the
      // user's retry. An upload still in flight in this tab is caught by filename instead.
      if (!asset.md5 || asset.status?.state === "AWAITING_UPLOAD") return;
      // The server may store the MD5 as hex; the client computes base64.
      const key = `${asset.filename}:${normalizeMD5ToBase64(asset.md5)}`;
      if (!libraryAssetIds.has(key)) libraryAssetIds.set(key, asset.id);
    };
    try {
      const searchResults = await Promise.all(
        uniqueFilenames.map(async (filename) => {
          try {
            // Search returns fuzzy matches — we filter for exact filename match below
            const results = await wire.assets.search(filename);
            return results;
          } catch {
            return [];
          }
        })
      );
      for (const results of searchResults) {
        for (const asset of results) addLibraryAsset(asset);
      }
    } catch {
      for (const asset of wire.assets.all()) addLibraryAsset(asset);
    }

    // Merge two sources of "already uploading" filenames:
    // 1. uploads() signal — files that have been added to the upload state
    // 2. filesInPipeline — files inside a concurrent uploadFiles call that
    //    haven't reached setUploads yet (closes the async race window)
    const currentUploads = uploads();
    const activeFromSignal = currentUploads
      .filter((u) => u.status === "pending" || u.status === "uploading" || u.status === "processing")
      .map((u) => u.file.name);
    const activeFromPipeline = [...filesInPipeline];
    const activeUploadFilenames = new Set([...activeFromSignal, ...activeFromPipeline]);

    // Track hashes within this batch to catch same file selected twice
    const batchHashes = new Set<string>();

    for (const file of files) {
      const validation = validateFile(file, config);
      if ("error" in validation) {
        errors.push(`${file.name}: ${validation.error}`);
        continue;
      }

      if (activeUploadFilenames.has(file.name)) {
        inQueue.push(file.name);
        // If the in-flight upload already has a server-assigned asset ID, surface
        // it so the caller can drop the file straight into chat scope without
        // waiting for upload completion.
        const matchingUpload = uploads().find((u) => u.file.name === file.name && u.assetId);
        if (matchingUpload?.assetId) inQueueAssetIds.push(matchingUpload.assetId);
        continue;
      }

      let fileHash: string | undefined;
      try {
        fileHash = await computeMD5Checksum(file);
      } catch {
        // MD5 computation failed — skip dedup checks for this file
      }

      if (fileHash) {
        const key = `${file.name}:${fileHash}`;
        const libraryAssetId = libraryAssetIds.get(key);
        if (libraryAssetId) {
          duplicates.push(file.name);
          duplicateAssetIds.push(libraryAssetId);
          continue;
        }

        // Within-batch dedup: skip if exact same file (name + content) already queued
        if (batchHashes.has(key)) {
          duplicates.push(file.name);
          continue;
        }
        batchHashes.add(key);
      }

      validFiles.push({ file, mimeType: validation.mimeType, md5Hash: fileHash });
    }

    // Register valid files in the pipeline immediately (synchronous) so that
    // any concurrent uploadFiles call will see them in activeUploadFilenames.
    for (const { file } of validFiles) {
      filesInPipeline.add(file.name);
    }

    if (errors.length > 0) {
      if (config.onError) {
        config.onError({
          type: "validation",
          message: errors.join("\n"),
          filenames: errors.map((e) => e.split(":")[0]),
        });
      }
    }

    if (duplicates.length > 0) {
      if (config.onError) {
        const message =
          duplicates.length === 1
            ? `The file "${duplicates[0]}" is already being uploaded or has already been uploaded.`
            : `The following ${duplicates.length} files are already being uploaded or have already been uploaded: ${duplicates.join(", ")}`;
        config.onError({
          type: "duplicate",
          message,
          filenames: duplicates,
          assetIds: duplicateAssetIds,
        });
      }
    }

    if (inQueue.length > 0) {
      if (config.onError) {
        const message =
          inQueue.length === 1
            ? `The file "${inQueue[0]}" is already queued for upload.`
            : `${inQueue.length} files are already queued for upload.`;
        config.onError({
          type: "in-queue",
          message,
          filenames: inQueue,
          assetIds: inQueueAssetIds,
        });
      }
    }

    if (validFiles.length === 0) {
      return;
    }

    const newUploads: FileUploadState[] = validFiles.map(({ file, mimeType, md5Hash }) => ({
      id: generateUploadId(),
      file,
      status: "pending" as const,
      projectId,
      md5Hash,
      mimeType,
    }));

    // Add to uploads list — now tracked by the uploads() signal so
    // filesInPipeline entries can be removed (they're only needed to
    // bridge the gap before this point).
    setUploads((prev) => [...newUploads, ...prev]);
    for (const upload of newUploads) {
      filesInPipeline.delete(upload.file.name);
    }

    const maxConcurrent = config.maxConcurrent || 3;
    const executing = new Set<Promise<void>>();

    for (const uploadState of newUploads) {
      const run = async (): Promise<void> => {
        await processFileUpload(uploadState);
        // Reached only after the await, by which point p below is assigned.
        executing.delete(p);
      };
      const p = run();
      executing.add(p);
      if (executing.size >= maxConcurrent) {
        await Promise.race(executing);
      }
    }
    await Promise.allSettled(executing);
  };

  const removeUploadByAssetId = (assetId: string): void => {
    const toRemove = uploads().filter((u) => u.assetId === assetId);
    for (const u of toRemove) {
      const controller = activeRequests.get(u.id);
      if (controller) {
        controller.abort();
        activeRequests.delete(u.id);
      }
    }
    setUploads((prev) => prev.filter((upload) => upload.assetId !== assetId));
  };

  onCleanup(() => {
    activeRequests.forEach((controller) => controller.abort());
    activeRequests.clear();
  });

  return {
    uploads,
    uploadFiles,
    tryGateUploadByCredits,
    removeUploadByAssetId,
  };
}
